import {
  hasExplicitLocaleChoice,
  shouldSkipEnglishLocaleSwitch,
  shouldUseEnglishFallback,
} from '@theme/newsletter-popup-logic';

const COUNTRY_REQUEST_TIMEOUT = 2500;
const COUNTRY_CACHE_KEY = 'kokoruno_detected_country_v2';
const CONFIG_SELECTOR = '[data-kokoruno-market-locale-guard]';

function readConfig() {
  try {
    const source = document.querySelector(CONFIG_SELECTOR)?.textContent || '{}';
    const config = JSON.parse(source);

    return {
      availableCountries: Array.isArray(config.availableCountries) ? config.availableCountries : [],
      countryCode: String(config.countryCode || '').trim().toUpperCase(),
      designMode: Boolean(config.designMode),
      locale: String(config.locale || '').trim(),
      root: String(config.root || '/').trim() || '/',
    };
  } catch (error) {
    return null;
  }
}

function readCachedCountry(locale) {
  try {
    const country = JSON.parse(sessionStorage.getItem(COUNTRY_CACHE_KEY) || '{}');
    const code = String(country.code || '').trim().toUpperCase();
    const name = String(country.name || '').trim();

    return /^[A-Z]{2}$/.test(code) && name && country.locale === locale ? { code, name } : null;
  } catch (error) {
    return null;
  }
}

function cacheCountry(country, locale) {
  try {
    sessionStorage.setItem(COUNTRY_CACHE_KEY, JSON.stringify({ ...country, locale }));
  } catch (error) {
    // Storage can be unavailable in private browsing; detection still works.
  }
}

async function detectSuggestedCountry(config) {
  const cachedCountry = readCachedCountry(config.locale);
  if (cachedCountry) return cachedCountry;

  const requestController = new AbortController();
  const requestTimer = window.setTimeout(() => requestController.abort(), COUNTRY_REQUEST_TIMEOUT);
  const excludeCountry = /^[A-Z]{2}$/.test(config.countryCode)
    ? `&country[exclude]=${encodeURIComponent(config.countryCode)}`
    : '';

  try {
    const response = await fetch(
      `${config.root.replace(/\/+$/, '')}/browsing_context_suggestions.json?country[enabled]=true${excludeCountry}`,
      {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: requestController.signal,
      }
    );
    if (!response.ok) return null;

    const payload = await response.json();
    const code = String(payload?.detected_values?.country?.handle || '').trim().toUpperCase();
    const name = String(payload?.detected_values?.country?.name || '').trim();
    if (!/^[A-Z]{2}$/.test(code) || !name) return null;

    const country = { code, name };
    cacheCountry(country, config.locale);
    return country;
  } catch (error) {
    return null;
  } finally {
    window.clearTimeout(requestTimer);
  }
}

function submitEnglishLocalization() {
  const form = document.getElementById('KokorunoAutomaticEnglishForm');
  if (!(form instanceof HTMLFormElement)) return false;

  form.requestSubmit();
  return true;
}

async function initializeMarketLocaleGuard(config, state) {
  const country = await detectSuggestedCountry(config);
  state.ensureEnglishForCountry(country);
}

function ensureEnglishForCountry(config, state, country) {
  if (country) state.country = country;
  if (state.switchingToEnglish || !country) return state.switchingToEnglish;

  if (
    !shouldSkipEnglishLocaleSwitch({ explicitChoice: hasExplicitLocaleChoice() }) &&
    shouldUseEnglishFallback({
      availableCountries: config.availableCountries,
      detectedCountryCode: country.code,
      locale: config.locale,
    })
  ) {
    state.switchingToEnglish = submitEnglishLocalization();
  }

  return state.switchingToEnglish;
}

const config = readConfig();

if (config && !config.designMode && !/^en(?:-|$)/i.test(config.locale)) {
  const state = {
    country: null,
    switchingToEnglish: false,
    ready: null,
    ensureEnglishForCountry: null,
  };

  state.ensureEnglishForCountry = (country) => ensureEnglishForCountry(config, state, country);
  state.ready = initializeMarketLocaleGuard(config, state);
  window.KokorunoMarketLocaleGuard = state;
}
